import {
  Box,
  Divider,
  TextField,
  FormHelperText,
  AutocompleteRenderInputParams,
} from "@mui/material";
import { ListSelectProps } from "./index";
import { COLOR_CONSTANTS } from "../../constants/styles";
import useLocale from "src/domains/shared/hooks/localeHook/useLocale";
import { useTranslation } from "react-i18next";

type RenderInputProps = {
  params: AutocompleteRenderInputParams;
} & Pick<
  ListSelectProps,
  | "placeholder"
  | "showDivider"
  | "showLabel"
  | "helperText"
  | "disabledTypeInput"
  | "ariaLabel"
>;

export function RenderInput({
  params,
  placeholder,
  showLabel = false,
  showDivider = false,
  helperText,
  disabledTypeInput = false,
  ariaLabel,
}: RenderInputProps) {
    const { t } = useTranslation("graph");
  const [locale] = useLocale();

  return (
    <Box sx={{ overflow: "visible" }}>
      <TextField
        {...params}
        sx={{
          ...textField,
          ...(disabledTypeInput && {
            "& input::selection": {
              backgroundColor: "transparent",
            },
          }),
        }}
        placeholder={placeholder}
        inputProps={{
          ...params.inputProps,
          readOnly: disabledTypeInput,
          ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
          style: {
            cursor: disabledTypeInput ? "pointer" : "text",
          },
        }}
        slotProps={{
          input: {
            sx: slotInput,
            ...params.InputProps,
            endAdornment: (
              <Box sx={endContainer}>
                {showLabel && (
                  <Box sx={chip}>{t("standard", { ns: "graph" })}</Box>
                )}
                {showDivider && <Divider orientation="vertical" sx={divider} />}
                {params.InputProps.endAdornment}
              </Box>
            ),
          },
        }}
      />
      {helperText && (
        <FormHelperText sx={{ helper }}>{helperText}</FormHelperText>
      )}
    </Box>
  );
}

const textField = {
  // overflowX: "auto",
  overflowY: "visible",
  "& .MuiOutlinedInput-root": {
    overflowY: "visible",
    padding: "0 30px 0 8px !important",
    "&::-webkit-scrollbar-button": {
      display: "none",
    },
    "& fieldset": {
      border: "none",
      backgroundColor: "#ffffff",
      zIndex: -1,
    },
    "& input::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    "& input::-webkit-outer-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    "& input[type='number']": {
      MozAppearance: "textfield",
    },
  },
  "& .MuiAutocomplete-endAdornment": {
    width: "16px",
    height: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0",
  },
} as const;

const chip = {
  width: "35px",
  height: "20px",
  padding: "2px 10px",
  whiteSpace: "nowrap",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "11px",
  fontWeight: 600,
  borderRadius: "4px",
  color: "rgba(29, 116, 255)",
  border: "1px solid rgba(29, 116, 255)",
  backgroundColor: "rgba(29, 116, 255, 0.1)",
  "& .MuiChip-label": {
    lineHeight: "16px",
  },
} as const;

const endContainer = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  cursor: "pointer",
} as const;

const divider = {
  height: "16px",
  cursor: "pointer",
} as const;

const slotInput = {
  border: `1px solid ${COLOR_CONSTANTS._components.input.outlined.enabledBorder}`,
  borderRadius: "4px",
  "&:hover": {
    border: `1px solid ${COLOR_CONSTANTS._components.input.outlined.hoverBorder}`,
  },
  "&:focus": {
    border: `2px solid ${COLOR_CONSTANTS.primary.main}`,
  },
  "&:disabled": {
    border: `1px solid ${COLOR_CONSTANTS._components.input.outlined.enabledBorder}`,
    background: COLOR_CONSTANTS._components.input.filled.enabledFill,
  },
} as const;

const helper = {
  marginLeft: "12px",
  fontSize: "12px",
  color: "rgba(0, 0, 0, 0.6)",
} as const;
